import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { IconButton } from '../components/UI';
import Icon from '../components/Icon';
import DailyStreakCard from '../components/DailyStreakCard';

interface Competition {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  kind: 'generic' | 'leaderboard' | 'checkin' | 'happyhour';
  durationType: 'daily' | 'weekly' | 'monthly' | 'custom';
  prize: string | null;
  ctaLabel: string | null;
  ctaTarget: string | null;
  isActive: boolean;
}

interface HappyHour {
  id: string;
  startAt: string;
  endAt: string;
  bonusPercent: number;
  status: 'upcoming' | 'active';
}

const KIND_ICON: Record<Competition['kind'], string> = {
  leaderboard: 'trophy',
  checkin: 'calendar-check',
  happyhour: 'bolt',
  generic: 'star',
};

const DURATION_LABEL: Record<Competition['durationType'], string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  custom: 'Limited',
};

// Map mobile route names (and a few common aliases) to web paths.
const ROUTE_MAP: Record<string, string> = {
  WalletTopUp: '/wallet-topup',
  'Add funds': '/topup',
  TopUp: '/topup',
  Withdraw: '/withdraw',
  Events: '/events',
  Profile: '/profile',
  Home: '/home',
};

const isUrl = (s: string) => /^https?:\/\//i.test(s.trim());

export function resolveTarget(target: string | null | undefined): { kind: 'url' | 'path' | 'none'; value: string } {
  const t = (target ?? '').trim();
  if (!t) return { kind: 'none', value: '' };
  if (isUrl(t)) return { kind: 'url', value: t };
  return { kind: 'path', value: ROUTE_MAP[t] ?? (t.startsWith('/') ? t : `/${t.toLowerCase()}`) };
}

export default function Events() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();

  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [happyHour, setHappyHour] = useState<HappyHour | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get<Competition[]>(`${API_BASE}/competitions`);
        if (active && Array.isArray(res.data)) setCompetitions(res.data.filter(c => c.isActive));
      } catch {
        // public best-effort call — show nothing extra on failure
      }
    })();
    return () => { active = false; };
  }, []);

  const fetchHappyHour = useCallback(async () => {
    try {
      const res = await axios.get<HappyHour | null>(`${API_BASE}/happy-hours/current`);
      setHappyHour(res.data && res.data.id ? res.data : null);
    } catch {
      // public best-effort call — falls back to "Soon" placeholder
    }
  }, []);

  useEffect(() => { fetchHappyHour(); }, [fetchHappyHour]);

  const happyHourSubtitle = (h: HappyHour): string => {
    const time = new Date(h.status === 'active' ? h.endAt : h.startAt)
      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const when = h.status === 'active' ? `ends ${time}` : `starts ${time}`;
    return `${h.bonusPercent}% bonus • ${when}`;
  };

  const openCompetition = (c: Competition) => {
    const r = resolveTarget(c.ctaTarget);
    if (r.kind === 'url') window.open(r.value, '_blank', 'noopener');
    else if (r.kind === 'path') navigate(r.value);
  };

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '14px 16px',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          backgroundColor: colors.header,
          borderBottom: `1px solid ${colors.headerBorder}`,
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <IconButton icon="arrow-left" accessibilityLabel={t('actions.back')} onPress={() => navigate(-1)} />
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>Events</div>
      </div>

      <div style={{ padding: 16, paddingBottom: 40 }}>
        {/* Daily Streak */}
        <DailyStreakCard />

        <div style={{ fontSize: 14, lineHeight: 1.45, color: colors.textMuted, marginBottom: 16 }}>
          Join live competitions and grab limited-time bonuses.
        </div>

        {/* Happy Hours */}
        {happyHour ? (
          <EventCard
            colors={colors}
            icon="bolt"
            title="Happy Hours"
            subtitle={happyHourSubtitle(happyHour)}
            badge={{ text: 'Live', tone: 'live' }}
          />
        ) : <p style={{ margin: '16px 0 24px', color: colors.textSub, fontSize: 15 }}>{t('components.events.noHappyHour')}</p>}

        {/* Admin-managed competitions */}
        {competitions.map(c => {
          const tappable = !!resolveTarget(c.ctaTarget).value;
          return (
            <EventCard
              key={c.id}
              colors={colors}
              icon={KIND_ICON[c.kind] ?? 'star'}
              imageUrl={c.imageUrl}
              title={c.title}
              subtitle={c.description ?? undefined}
              badge={{ text: 'Live', tone: 'live' }}
              meta={[
                DURATION_LABEL[c.durationType] ?? c.durationType,
                ...(c.prize ? [c.prize] : []),
              ]}
              onClick={tappable ? () => openCompetition(c) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

function EventCard({
  colors, icon, imageUrl, title, subtitle, badge, meta, onClick, dim,
}: {
  colors: ThemeColors;
  icon: string;
  imageUrl?: string | null;
  title: string;
  subtitle?: string;
  badge: { text: string; tone: 'live' | 'soon' };
  meta?: string[];
  onClick?: () => void;
  dim?: boolean;
}) {
  const clickable = !!onClick;
  const badgeBg = badge.tone === 'live' ? colors.accentSoft : colors.cardBorder;
  const badgeColor = badge.tone === 'live' ? colors.accentText : colors.textMuted;

  const cardStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 14,
    borderRadius: 16, border: `1px solid ${colors.cardBorder}`,
    backgroundColor: colors.card, padding: 16, marginBottom: 12,
    width: '100%', textAlign: 'left', boxSizing: 'border-box',
    cursor: clickable ? 'pointer' : 'default',
    opacity: dim ? 0.6 : 1,
    fontFamily: 'inherit',
  };

  const content = (
    <>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          style={{ width: 46, height: 46, borderRadius: 13, objectFit: 'cover', flexShrink: 0, backgroundColor: colors.surfaceMuted }}
        />
      ) : (
        <div style={{
          width: 46, height: 46, borderRadius: 13,
          backgroundColor: colors.accentSoft,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name={icon} size={20} color={colors.accentText} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <div style={{
            fontSize: 15, fontWeight: 800, color: colors.text,
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {title}
          </div>
          <div style={{ backgroundColor: badgeBg, borderRadius: 999, padding: '2px 8px', flexShrink: 0 }}>
            <div style={{
              fontSize: 14, fontWeight: 900, color: badgeColor,
              letterSpacing: 0.5, textTransform: 'uppercase',
            }}>
              {badge.text}
            </div>
          </div>
        </div>
        {subtitle && (
          <div style={{ fontSize: 14, lineHeight: 1.45, color: colors.textMuted }}>{subtitle}</div>
        )}
        {meta && meta.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {meta.map((m, i) => (
              <div key={i} style={{
                backgroundColor: i === 0 ? colors.accentSoft : colors.cardBorder,
                borderRadius: 8, padding: '3px 8px', maxWidth: 180,
              }}>
                <div style={{
                  fontSize: 14, fontWeight: 700,
                  color: i === 0 ? colors.accentText : colors.textMuted,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {m}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {clickable && <Icon name="chevron-right" size={14} color={colors.textMuted} />}
    </>
  );

  if (clickable) {
    return <button onClick={onClick} style={cardStyle}>{content}</button>;
  }
  return <div style={cardStyle}>{content}</div>;
}
